import bboxPolygon from "@turf/bbox-polygon";
import mask from "@turf/mask";
import { Source, Layer, MapLayerMouseEvent } from "react-map-gl";
import { Map, MapScenePreview, InferencesLayer } from "../../components";
import { useNavigate } from "react-router-dom";
import { Feature } from "geojson";
import { InferenceCollection } from "../../types/inferences";

type ValidateMapProps = {
  sceneId: string;
  brightness: number;
  contrast: number;
  bboxMask: boolean;
  compositeMode: boolean;
  bounds?: [number, number, number, number];
  selectedInference: Feature;
  inferences: InferenceCollection;
  isValidated: boolean;
}
function ValidateMap({
  bounds,
  sceneId,
  brightness,
  contrast,
  bboxMask,
  compositeMode,
  selectedInference,
  inferences,
  isValidated,
}: ValidateMapProps ) {
  const navigate = useNavigate();
  const handleMapClick = (event: MapLayerMouseEvent) => {
    const clickedFeature = event.features?.find(
      (f) => f.layer.id === "inferences-polygons" || f.layer.id === "inferences-lines"
    );
    if (clickedFeature) {
      let inferenceId: number | undefined;
      if (compositeMode) {
        // MapboxGL serializes array properties as JSON strings
        const inferenceIds = JSON.parse(clickedFeature.properties?.inference_ids ?? "null")
          ?? JSON.parse(clickedFeature.properties?.ids ?? "null");
        inferenceId = inferenceIds?.[0];
      } else {
        inferenceId = clickedFeature.properties?.id;
      }
      if (inferenceId !== undefined) {
        navigate(`/scenes/${sceneId}/validate/${inferenceId}`);
      }
    }
  };
  return (
    <Map
      bounds={bounds}
      boundsPadding={200}
      zoomDuration={2000}
      miniMap
      onClick={handleMapClick}
      interactiveLayerIds={["inferences", "inferences-centroids", "inferences-polygons"]}
      basemapSwitcher
    >
      <MapScenePreview
        id={sceneId}
        brightness={brightness}
        contrast={contrast}
      />
      {bboxMask && (
        <Source
          id="inference-bbbox-mask"
          type="geojson"
          data={mask(bboxPolygon(selectedInference.bbox!))} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        >
          <Layer
            id="inference-bbbox-mask-layer"
            beforeId="inference-bbbox-layer"
            type="fill"
            paint={{
              "fill-color": "rgba(0,0,0,0.3)",
            }}
          />
        </Source>
      )}
      <Source
        id="inference-bbbox-source"
        type="geojson"
        data={bboxPolygon(selectedInference.bbox!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
      >
        <Layer
          id="inference-bbbox-layer"
          type="line"
          paint={{
            "line-color": isValidated
              ? selectedInference?.properties?.is_valid
                ? "#48BB78"
                : "#F56565"
              : "#1ed0f2",
            "line-width":  [
              "interpolate",
              ["linear"], ["zoom"],
              0,
              2,
              22,
              8
            ],
          }}
        />
      </Source>
      {inferences && <InferencesLayer id="inferences" data={inferences} />}
    </Map>
  );
}

export default ValidateMap;
